import { Vector3 } from '@technology/web3d';
import { UnreachableCaseError } from 'ts-essentials';
import { LineStyle } from '../measurements/measurement';
import type {
  GeoJsonLike,
  GeofenceCoordinateSpace,
  GeofencePolygonEmphasis,
  GeofencePolygonSkipReason,
  GeofencePolygonStyle,
} from './geofence-polygon.types';

export const DEFAULT_GEOFENCE_POLYGON_STYLE: Readonly<GeofencePolygonStyle> = {
  color: '#E31C79',
  fillOpacity: 0.25,
  lineThickness: 3,
  lineStyle: LineStyle.Solid,
};

const MIN_LINE_THICKNESS = 0.1;
const HIGHLIGHTED_THICKNESS_DELTA = 1;
const SELECTED_THICKNESS_DELTA = 2;
const HIGHLIGHTED_OPACITY_DELTA = 0.15;
const SELECTED_OPACITY_DELTA = 0.3;
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function validColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value);
}

export type { GeofencePolygonStyle } from './geofence-polygon.types';
function validLineStyle(value: unknown): value is LineStyle {
  return (
    value === LineStyle.Solid ||
    value === LineStyle.Dashed ||
    value === LineStyle.Dotted
  );
}
function resolveOpacity(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

function resolveThickness(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return value >= MIN_LINE_THICKNESS ? value : fallback;
}

export function resolveGeofencePolygonStyle(
  base?: Partial<GeofencePolygonStyle>,
  override?: Partial<GeofencePolygonStyle>
): GeofencePolygonStyle {
  const color = validColor(override?.color)
    ? override.color
    : validColor(base?.color)
      ? base.color
      : DEFAULT_GEOFENCE_POLYGON_STYLE.color;
  const fillOpacity = resolveOpacity(
    override?.fillOpacity,
    resolveOpacity(
      base?.fillOpacity,
      DEFAULT_GEOFENCE_POLYGON_STYLE.fillOpacity
    )
  );
  const lineThickness = resolveThickness(
    override?.lineThickness,
    resolveThickness(
      base?.lineThickness,
      DEFAULT_GEOFENCE_POLYGON_STYLE.lineThickness
    )
  );
  const lineStyle = validLineStyle(override?.lineStyle)
    ? override.lineStyle
    : validLineStyle(base?.lineStyle)
      ? base.lineStyle
      : DEFAULT_GEOFENCE_POLYGON_STYLE.lineStyle;

  return { color, fillOpacity, lineThickness, lineStyle };
}

export function emphasizeStyle(
  style: GeofencePolygonStyle,
  emphasis: GeofencePolygonEmphasis
): GeofencePolygonStyle {
  switch (emphasis) {
    case 'none':
      return style;
    case 'highlighted':
      return {
        ...style,
        lineThickness: style.lineThickness + HIGHLIGHTED_THICKNESS_DELTA,
        fillOpacity: Math.min(1, style.fillOpacity + HIGHLIGHTED_OPACITY_DELTA),
      };
    case 'selected':
      return {
        ...style,
        lineThickness: style.lineThickness + SELECTED_THICKNESS_DELTA,
        fillOpacity: Math.min(1, style.fillOpacity + SELECTED_OPACITY_DELTA),
      };
    default:
      throw new UnreachableCaseError(emphasis);
  }
}

const MIN_VERTEX_DISTANCE = 0.02;
const MIN_POSITION_LENGTH = 2;
const MIN_DISTINCT_VERTICES = 3;
const MAX_LONGITUDE = 180;
const MAX_LATITUDE = 90;

export function normalizeGeoJsonType(type: string): string {
  const separator = type.lastIndexOf('_');
  return separator >= 0 ? type.slice(separator + 1) : type;
}

type RingsResult =
  | { readonly rings: readonly (readonly [number, number][])[] }
  | { readonly skipped: GeofencePolygonSkipReason };

function isPosition(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= MIN_POSITION_LENGTH &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number' &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1])
  );
}

function readRing(value: unknown): readonly [number, number][] | undefined {
  if (!Array.isArray(value) || !value.every(isPosition)) return undefined;
  return value.map(([lon, lat]) => [lon, lat]);
}

function readPolygonCoordinates(
  coordinates: unknown,
  multi: boolean
): readonly (readonly [number, number][])[] | undefined {
  if (!Array.isArray(coordinates)) return undefined;
  if (!multi) {
    const ring = readRing(coordinates[0]);
    return ring ? [ring] : undefined;
  }
  const rings: Array<readonly [number, number][]> = [];
  for (const polygon of coordinates) {
    const ring = Array.isArray(polygon) ? readRing(polygon[0]) : undefined;
    if (!ring) return undefined;
    rings.push(ring);
  }
  return rings;
}

function extract(geojson: GeoJsonLike): RingsResult {
  const type = normalizeGeoJsonType(geojson.type);
  if (type === 'Feature') {
    return geojson.geometry
      ? extract(geojson.geometry)
      : { skipped: 'invalid-coordinates' };
  }
  if (type === 'FeatureCollection') {
    const features = geojson.features;
    if (!features) return { skipped: 'invalid-coordinates' };
    const rings: Array<readonly [number, number][]> = [];
    for (const feature of features) {
      const result = extract(feature);
      if ('skipped' in result) return result;
      rings.push(...result.rings);
    }
    return { rings };
  }
  if (type !== 'Polygon' && type !== 'MultiPolygon') {
    return { skipped: 'unsupported-geometry' };
  }
  const rings = readPolygonCoordinates(
    geojson.coordinates,
    type === 'MultiPolygon'
  );
  return rings ? { rings } : { skipped: 'invalid-coordinates' };
}

export function extractExteriorRings(
  geojson: GeoJsonLike,
  coordinateSpace: GeofenceCoordinateSpace = 'lonlat'
): RingsResult {
  // The markup plugin triangulates one contour; interior rings are intentionally ignored.
  const result = extract(geojson);
  if (coordinateSpace === 'lonlat' && 'rings' in result) {
    const inRange = result.rings.every((ring) =>
      ring.every(
        ([lon, lat]) =>
          Math.abs(lon) <= MAX_LONGITUDE && Math.abs(lat) <= MAX_LATITUDE
      )
    );
    if (!inRange) return { skipped: 'invalid-coordinates' };
  }
  return result;
}

export function closeRing(
  points: readonly Vector3[],
  minDistance = MIN_VERTEX_DISTANCE
): Vector3[] | undefined {
  const distinct: Vector3[] = [];
  for (const point of points) {
    const previous = distinct.at(-1);
    if (
      !previous ||
      previous.distanceToSquared(point) >= minDistance * minDistance
    ) {
      distinct.push(point.clone());
    }
  }
  while (distinct.length > 1) {
    const last = distinct.at(-1);
    if (
      last === undefined ||
      distinct[0].distanceToSquared(last) >= minDistance * minDistance
    ) {
      break;
    }
    distinct.pop();
  }
  if (distinct.length < MIN_DISTINCT_VERTICES) return undefined;
  return [...distinct, distinct[0].clone()];
}

export function avoidanceZonePolygonId(
  zoneId: string,
  geofenceId: string
): string {
  return `${zoneId}:${geofenceId}`;
}

export function importGeofencePolygonId(name: string): string {
  return `import:${name}`;
}

// The version is part of the id so pending and published versions stay unique in the polygon layer.
export function approvalGeofencePolygonId(
  geofenceId: string,
  versionNum: number
): string {
  return `approval:${geofenceId}:${versionNum}`;
}
